/**
 * The catalog list (plan docs/plans/lms-plan.md 8.1 "lists courses by
 * category with the current version, the draft if any, the number of people
 * who hold it, and the validity"; 6.5, 6.6, 18.17; WP-44; R7, R8, R10), the
 * index of /lms/manage/catalog.
 *
 * - GET /courses?all=1 (the admin view: every course of the network, its
 *   origin and its open draft). The list route carries no holder count, so
 *   the number of holders comes from each open draft's publish impact (GET
 *   /admin/versions/:id impact.valid_completions and open_assignments, the
 *   numbers the publish dialog shows, 8.1); a course with no open draft shows
 *   a dash (NOTES, WP-44 report).
 * - The built-in catalog card: GET /admin/catalog/status's pending count,
 *   and Install (POST /admin/catalog/install: every new or changed builtin
 *   file as a DRAFT, never published, G8; the suggested profiles, empty).
 * - New course and New scenario (NewCourseDialog).
 * - Build from documents (the course builder's wizard, builder plan 8.1) and
 *   a "Building" pill on a course with an active builder run (GET
 *   /admin/builder/runs), both only when GET /health says the builder is on
 *   for the network (health.builder.enabled and network_allowed, G20).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Download, FileUp, Library, Loader2, Plus, Sparkles } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import SearchInput from '../../../../ui/SearchInput';
import { useToast } from '../../../../ui/Toast';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { listBuilderRuns } from '../../../api/builder';
import { getCatalogStatus, getVersion, installCatalog, listAdminCourses } from '../../../api/catalog';
import type { AdminCourseItem, CatalogInstallResult, CatalogStatus, PublishImpact } from '../../../api/catalog';
import type { CourseCategory } from '../../../content/types';
import { noticeCls, pillCls } from '../../../components/player/styles';
import { builderPaths, isBuildingStatus } from './builder/runModel';
import { useBuilderAvailability } from './builder/useBuildRun';
import { CATEGORY_LABELS, CATEGORY_ORDER, catalogPaths, formatDate, KIND_LABELS, validityLabel } from './model';
import { NewCourseDialog } from './NewCourseDialog';
import { cardCls, hintCls, linkCls, microLabelCls, rowCls, subheadingCls, tableWrapCls, tdCls, thCls } from './styles';

/** How many draft details load at once for the holder counts. */
const IMPACT_CONCURRENCY = 3;

type ImpactState = PublishImpact | 'loading' | 'error';

const FILE_STATUS_WORDS: Record<string, string> = {
  not_installed: 'Not installed',
  up_to_date: 'Up to date',
  changed: 'Changed since install',
  draft_open: 'Blocked: an edited draft is open',
  slug_taken: 'Blocked: a custom course holds the slug',
  course_inactive: 'Course switched off',
};

function installSummary(result: CatalogInstallResult): string {
  const parts = [`${result.installed.length} installed as draft${result.installed.length === 1 ? '' : 's'}`];
  if (result.skipped.length > 0) parts.push(`${result.skipped.length} skipped`);
  if (result.blocked && result.blocked.length > 0) parts.push(`${result.blocked.length} blocked by an edited draft`);
  if (result.profiles) parts.push(`${result.profiles.created} profile${result.profiles.created === 1 ? '' : 's'} created`);
  return `Catalog install: ${parts.join(', ')}. Nothing was published: read each draft and publish it (G8).`;
}

function HoldersCell({ course, impact }: { course: AdminCourseItem; impact: ImpactState | undefined }) {
  if (!course.draft_version) {
    return (
      <span className="text-gray-400 dark:text-gray-500" title="Holder counts are shown while a draft is open (its publish impact).">
        {'\u2014'}
      </span>
    );
  }
  if (!impact || impact === 'loading') return <span className="text-xs text-gray-400 dark:text-gray-500">Counting...</span>;
  if (impact === 'error') return <span className="text-xs text-red-700 dark:text-red-400">Not available</span>;
  return (
    <span className="whitespace-nowrap" data-testid={`lms-holders-${course.id}`}>
      <span className="font-medium tabular-nums">{impact.valid_completions.toLocaleString()}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{' hold it'}</span>
      <span className="block text-xs text-gray-500 dark:text-gray-400">{`${impact.open_assignments.toLocaleString()} assigned`}</span>
    </span>
  );
}

function CatalogCard({
  status,
  statusError,
  onInstall,
  installing,
}: {
  status: CatalogStatus | null;
  statusError: string | null;
  onInstall: () => void;
  installing: boolean;
}) {
  const pending = status ? status.pending : null;
  const open = status ? status.files.filter((f) => f.status !== 'up_to_date') : [];
  return (
    <section className={`${cardCls} space-y-3`} data-testid="lms-catalog-install" aria-labelledby="lms-catalog-install-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <h3 id="lms-catalog-install-title" className={`flex items-center gap-2 ${subheadingCls}`}>
            <Library className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Built-in catalog
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="lms-catalog-pending">
            {statusError
              ? statusError
              : pending === null
                ? 'Checking the built-in files...'
                : pending === 0
                  ? 'Every built-in course is installed and up to date.'
                  : `${pending} built-in file${pending === 1 ? '' : 's'} waiting to install or update.`}
          </p>
          <p className={hintCls}>Installing adds new and changed built-in courses as drafts; nothing is published until a person reads and publishes it (6.6, G8).</p>
        </div>
        <button
          type="button"
          className={primaryBtnCls}
          onClick={onInstall}
          disabled={installing || pending === null || pending === 0}
          data-testid="lms-catalog-install-button"
        >
          {installing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
          {pending ? `Install (${pending})` : 'Install'}
        </button>
      </div>
      {open.length > 0 ? (
        <details className="group">
          <summary className="inline-flex min-h-[44px] cursor-pointer items-center text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 sm:min-h-0">
            {`Files that are not up to date (${open.length})`}
          </summary>
          <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">
            {open.map((f) => (
              <li key={f.slug} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{f.slug}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{FILE_STATUS_WORDS[f.status] ?? f.status}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

export function CourseList() {
  const { toast } = useToast();
  const [courses, setCourses] = useState<AdminCourseItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<CatalogStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [impacts, setImpacts] = useState<Record<string, ImpactState>>({});
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('');
  const [dialog, setDialog] = useState<'course' | 'scenario' | null>(null);
  const [confirmInstall, setConfirmInstall] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [tick, setTick] = useState(0);
  // Builder plan 8.1, G20: the builder's entry and its "Building" pills only when GET /health says it is on.
  const builder = useBuilderAvailability();
  const [building, setBuilding] = useState<Set<string>>(() => new Set());

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!builder.available) return undefined;
    let cancelled = false;
    listBuilderRuns({ limit: 200 })
      .then((list) => {
        if (cancelled) return;
        const ids = new Set<string>();
        (list.items || []).forEach((r) => {
          if (r.course_id && isBuildingStatus(r.status)) ids.add(r.course_id);
        });
        setBuilding(ids);
      })
      .catch(() => {
        // The pill is a convenience; the course page and the editor read the run themselves.
      });
    return () => {
      cancelled = true;
    };
  }, [builder.available, tick]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listAdminCourses()
      .then((items) => {
        if (cancelled) return;
        setCourses(items);
        // Holder counts: each open draft's publish impact, a few at a time.
        const drafts = items.filter((c) => c.draft_version);
        setImpacts(Object.fromEntries(drafts.map((c) => [c.id, 'loading' as ImpactState])));
        let next = 0;
        const worker = async () => {
          while (!cancelled && next < drafts.length) {
            const course = drafts[next];
            next += 1;
            try {
              // eslint-disable-next-line no-await-in-loop
              const detail = await getVersion(course.draft_version!.id);
              if (!cancelled) setImpacts((all) => ({ ...all, [course.id]: detail.impact ?? 'error' }));
            } catch {
              if (!cancelled) setImpacts((all) => ({ ...all, [course.id]: 'error' }));
            }
          }
        };
        for (let i = 0; i < Math.min(IMPACT_CONCURRENCY, drafts.length); i += 1) void worker();
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The catalog could not be loaded.'));
      });
    getCatalogStatus()
      .then((s) => {
        if (!cancelled) {
          setStatus(s);
          setStatusError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setStatusError(apiErrorMessage(err, 'The built-in catalog status could not be read.'));
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const install = async () => {
    setInstalling(true);
    try {
      const result = await installCatalog();
      toast(installSummary(result));
      setConfirmInstall(false);
      reload();
    } catch (err) {
      toast(apiErrorMessage(err, 'The catalog could not be installed.'), { type: 'error', duration: 0 });
      setConfirmInstall(false);
    } finally {
      setInstalling(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (courses || []).filter((c) => {
      if (category && c.category !== category) return false;
      if (!q) return true;
      return c.title.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
    });
  }, [courses, query, category]);

  const groups = CATEGORY_ORDER.map((cat) => ({ category: cat, items: filtered.filter((c) => c.category === cat) })).filter(
    (g) => g.items.length > 0
  );

  return (
    <div className="space-y-4" data-testid="lms-catalog-list">
      <CatalogCard status={status} statusError={statusError} onInstall={() => setConfirmInstall(true)} installing={installing} />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Search courses" label="Search courses" className="min-w-[12rem] flex-1 basis-56" />
        <FilterSelect
          label="Category"
          auto
          value={category}
          onChange={setCategory}
          options={[{ value: '', label: 'All categories' }, ...CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))]}
        />
        <button type="button" className={secondaryBtnCls} onClick={() => setDialog('scenario')} data-testid="lms-new-scenario">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          New scenario
        </button>
        <button type="button" className={primaryBtnCls} onClick={() => setDialog('course')} data-testid="lms-new-course">
          <Plus className="h-4 w-4" aria-hidden="true" />
          New course
        </button>
        {builder.available ? (
          <Link to={builderPaths.build()} className={secondaryBtnCls} data-testid="lms-builder-entry">
            <FileUp className="h-4 w-4" aria-hidden="true" />
            Build from documents
          </Link>
        ) : null}
      </div>

      {error ? (
        <div className="space-y-2">
          <div className={noticeCls('red')} role="alert" data-testid="lms-catalog-error">
            {error}
          </div>
          <button type="button" className={outlineBtnCls()} onClick={reload}>
            Retry
          </button>
        </div>
      ) : null}

      {!error && courses === null ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading the catalog...
        </div>
      ) : null}

      {courses !== null && courses.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No courses yet"
          hint="Install the built-in catalog above, or create a course or a scenario."
          variant="card"
        />
      ) : null}

      {courses !== null && courses.length > 0 && groups.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No course matches the search.</p>
      ) : null}

      {groups.map((group) => (
        <section key={group.category} className="space-y-2" data-testid={`lms-catalog-group-${group.category}`} aria-label={CATEGORY_LABELS[group.category as CourseCategory]}>
          <div className={microLabelCls}>{`${CATEGORY_LABELS[group.category as CourseCategory]} (${group.items.length})`}</div>
          <div className={tableWrapCls}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" className={thCls}>
                    Course
                  </th>
                  <th scope="col" className={thCls}>
                    Current version
                  </th>
                  <th scope="col" className={thCls}>
                    Draft
                  </th>
                  <th scope="col" className={thCls}>
                    Holders
                  </th>
                  <th scope="col" className={thCls}>
                    Validity
                  </th>
                  <th scope="col" className={thCls}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {group.items.map((c) => (
                  <tr key={c.id} className={rowCls} data-testid={`lms-catalog-row-${c.id}`}>
                    <td className={tdCls}>
                      <Link to={catalogPaths.course(c.id)} className={linkCls}>
                        {c.title}
                      </Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {`${KIND_LABELS[c.kind]}${c.origin === 'builtin' ? ' \u00b7 built-in' : ''}`}
                      </div>
                    </td>
                    <td className={tdCls}>
                      {c.current_version ? (
                        <span className="whitespace-nowrap">
                          {`Version ${c.current_version.version_no}`}
                          <span className="block text-xs text-gray-500 dark:text-gray-400">{formatDate(c.current_version.published_at)}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Not published</span>
                      )}
                    </td>
                    <td className={tdCls}>
                      {c.draft_version ? (
                        <Link to={catalogPaths.version(c.draft_version.id)} className={pillCls('amber')} data-testid={`lms-draft-${c.id}`}>
                          {`Draft v${c.draft_version.version_no}`}
                        </Link>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">{'\u2014'}</span>
                      )}
                    </td>
                    <td className={tdCls}>
                      <HoldersCell course={c} impact={impacts[c.id]} />
                    </td>
                    <td className={`${tdCls} whitespace-nowrap`}>{validityLabel(c.validity_months)}</td>
                    <td className={tdCls}>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span className={pillCls(c.is_active ? 'green' : 'gray')}>{c.is_active ? 'Active' : 'Switched off'}</span>
                        {building.has(c.id) ? (
                          <span className={pillCls('primary')} data-testid={`lms-building-${c.id}`}>
                            Building
                          </span>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {courses !== null && courses.length > 0 ? (
        <p className={hintCls}>Holder counts come from each open draft's publish impact; a course with no open draft shows a dash.</p>
      ) : null}

      <NewCourseDialog isOpen={dialog !== null} onClose={() => setDialog(null)} variant={dialog === 'scenario' ? 'scenario' : 'course'} />

      <ConfirmModal
        isOpen={confirmInstall}
        onClose={() => setConfirmInstall(false)}
        onConfirm={install}
        title="Install the built-in catalog?"
        message={`${status ? status.pending : 0} built-in file(s) will be added or updated as drafts, with the suggested profiles (empty). Nothing is published.`}
        confirmLabel="Install"
        loading={installing}
      />
    </div>
  );
}

export default CourseList;
